import { useEffect, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import styled from 'styled-components'
import { useGetQuery, usePostQuery } from 'hooks'
import { apiFetch } from 'actions/apiFetch'
import {
  Box,
  Button,
  Card,
  Dropdown,
  FormInput,
  Label,
  SearchBar,
  Tag
} from 'components'

const LocationContainer = styled(Box)`
  position: relative;
  display: flex;
  flex: 1;
  flex-basis: 100%;
  justify-content: stretch;
  align-items: stretch;
  align-content: stretch;
  flex-direction: column;
`

const LocationResults = styled.div`
  flex: 1;
  max-height: 2rem;
  oveflow: hidden;
  position: absolute;
  padding-top: 2.5rem;
  z-index: 9999;
  border-radius: 0.5rem;
  display: flex;
  flex-direction: column;
  justify-content: stretch;
  align-items: stretch;
  align-content: stretch;
`

const Location = styled.div`
  ${({ theme: { colors } }) => `
  height: 2.5rem;
  padding: 0.5rem;
  background: ${colors('chrome')};
  flex: 1;
  `}
`

const LocationSearch = ({ userId, userRole, onAssigned }) => {
  const [term, setTerm] = useState('')
  const { post: assignLocation } = usePostQuery({ url: '/admin/users_locations' })
  const { data: placesData, setPath, setEnabled } = useGetQuery({ enabled: false, url: '/admin/locations?q=' })

  const searchHandler = ({ target: { value } }) => {
    setTerm(value)
    setEnabled(true)
    setPath(`/admin/locations?q=${encodeURIComponent(value)}&limit=10&offset=0`)
  }

  const selectLocation = async (location) => {
    await assignLocation({ user_id: userId, location_id: location.id, role: userRole })
    setTerm('')
    onAssigned()
  }

  return (
    <LocationContainer>
      <SearchBar onChange={searchHandler} />
      <LocationResults>
        {term &&
          placesData?.locations?.rows?.map((place) => (
            <Location key={place.id} onClick={() => selectLocation(place)}>
              {place.name}
            </Location>
          ))}
      </LocationResults>
    </LocationContainer>
  )
}

export const AdminUserEdit = () => {
  const { slug } = useParams()
  const navigate = useNavigate()
  const { data, refetch } = useGetQuery({ url: `/admin/users/${slug}` })
  const { post: saveUser } = usePostQuery({ url: `/admin/users/${slug}`, method: 'PUT' })

  const user = data?.user
  const [state, setState] = useState(null)
  const [selectedRole, setSelectedRole] = useState(null)
  const [removedLocations, setRemovedLocations] = useState([])

  useEffect(() => {
    if (user) {
      setState(user)
      setSelectedRole(user.roles?.[0]?.slug ?? null)
      setRemovedLocations([])
    }
  }, [user])

  const handler = ({ target: { name, value } }) => {
    setState(prev => ({ ...prev, [name]: value }))
  }

  const roleHandler = ({ target: { value } }) => {
    setSelectedRole(value)
  }

  const removeLocation = (locationId) => {
    setRemovedLocations(prev => [...prev, locationId])
  }

  const handleSave = async () => {
    if (selectedRole && selectedRole !== user?.roles?.[0]?.slug) {
      await apiFetch({ path: `/admin/roles/${encodeURIComponent(selectedRole)}/users/${slug}`, method: 'PUT' })
    }
    await Promise.all(
      removedLocations.map(location_id =>
        apiFetch({ path: '/admin/users_locations', method: 'DELETE', payload: { user_id: user.id, location_id } })
      )
    )
    await saveUser({ can_respawn: state.can_respawn })
    navigate('/admin/users')
  }

  if (!user || !state) return null

  return (
    <>
      <Card>
        <FormInput
          label="Email"
          name="email"
          onChange={handler}
          value={state?.email}
          readonly={true}
        />
        <FormInput
          label="Full Name"
          name="full_name"
          onChange={handler}
          value={state?.full_name}
        />
        <Box margin="0.5rem">
          <Label for="role">Role</Label>
          <Dropdown
            name="role"
            data={[
              ['Admin', 'admin'],
              ['Host Owner', 'host:owner'],
              ['Host Manager', 'host:manager'],
              ['User', 'user']
            ]}
            onChange={roleHandler}
            value={selectedRole}
          />
        </Box>
        <Box margin="0.5rem">
          <Label for="locations">Locations</Label>
          {selectedRole?.startsWith('host:') ? (
            <LocationSearch
              userId={user.id}
              userRole={selectedRole}
              onAssigned={() => refetch(true)}
            />
          ) : (
            <Box margin="0.25rem 0" css="opacity:0.4;font-size:0.9em;">Select a host role to assign locations.</Box>
          )}
          <Box>
            {state?.user_locations
              ?.filter(l => !removedLocations.includes(l.id))
              .map((location) => (
                <Tag key={location.id} onClick={() => removeLocation(location.id)} style={{ cursor: 'pointer' }}>
                  {location.name} ×
                </Tag>
              ))}
          </Box>
        </Box>
        <Box margin="0.5rem">
          <Label>
            <input
              type="checkbox"
              name="can_respawn"
              checked={!!state?.can_respawn}
              onChange={({ target: { name, checked } }) =>
                setState(prev => ({ ...prev, [name]: checked }))
              }
              style={{ marginRight: '0.5rem' }}
            />
            Can Respawn (dungeon monsters &amp; items)
          </Label>
        </Box>
        <Button onPress={handleSave}>Save</Button>
      </Card>
    </>
  )
}
